import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import { TRIAL_LABEL } from "../jiraBadges";
import { explainFailure, stepToEnglish, stepToScript } from "../stepEnglish";
import RaiseBugModal from "./RaiseBugModal";
import { LinkedChip, RunIcon } from "./TestRunsPanel";

/* One execution, opened from a card on the Test Runs page: what ran, the
   result, and every step with its before/after screenshots — failures first
   to the eye (banner + red steps), nothing else. */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";
const ACTIVE = new Set(["queued", "running"]);

const STEP_TONE = {
  passed: { label: "Passed", pill: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", solid: "bg-emerald-500", Icon: Icons.Check, row: "border-slate-200" },
  failed: { label: "Failed", pill: "bg-rose-50 text-rose-700 ring-rose-600/20", solid: "bg-rose-500", Icon: Icons.X, row: "border-rose-200 ring-1 ring-rose-500/10" },
  skipped: { label: "Skipped", pill: "bg-amber-50 text-amber-700 ring-amber-600/20", solid: "bg-amber-400", Icon: Icons.Minus, row: "border-slate-200" },
  not_run: { label: "Not executed", pill: "bg-slate-100 text-slate-500 ring-slate-400/20", solid: "bg-slate-300", Icon: Icons.Minus, row: "border-slate-200 opacity-70" },
};

const RUN_TONE = {
  passed: { label: "Passed", pill: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", solid: "bg-emerald-500", Icon: Icons.Check },
  failed: { label: "Failed", pill: "bg-rose-50 text-rose-700 ring-rose-600/20", solid: "bg-rose-500", Icon: Icons.X },
  error: { label: "Error", pill: "bg-rose-50 text-rose-700 ring-rose-600/20", solid: "bg-rose-500", Icon: Icons.Alert },
  running: { label: "Executing", pill: "bg-blue-50 text-blue-700 ring-blue-600/20", solid: "bg-blue-500", Icon: Icons.Spinner, spin: true },
  queued: { label: "Queued", pill: "bg-slate-100 text-slate-600 ring-slate-500/20", solid: "bg-slate-400", Icon: Icons.Clock },
  ready: { label: "Not executed", pill: "bg-blue-50 text-blue-700 ring-blue-600/20", solid: "bg-blue-500", Icon: Icons.Spark },
  cancelled: { label: "Stopped", pill: "bg-slate-100 text-slate-600 ring-slate-500/20", solid: "bg-slate-400", Icon: Icons.Pause },
  interrupted: { label: "Interrupted", pill: "bg-amber-50 text-amber-800 ring-amber-600/30", solid: "bg-amber-500", Icon: Icons.Alert },
  blocked: { label: "Blocked", pill: "bg-amber-50 text-amber-800 ring-amber-600/30", solid: "bg-amber-500", Icon: Icons.Alert },
  skipped: { label: "Skipped", pill: "bg-amber-50 text-amber-800 ring-amber-600/30", solid: "bg-amber-500", Icon: Icons.Minus },
};

function fmtWhen(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function fmtDuration(a, b) {
  if (!a || !b) return "";
  const s = Math.max(0, Math.round((new Date(b) - new Date(a)) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

function initials(name) {
  const parts = String(name || "").trim().split(/[\s._@-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
}

const shotUrl = (key) => `${API_BASE_URL}executions/step-evidence/${encodeURIComponent(key)}`;

/* Full-size screenshot, with ← / → through every screenshot of the execution. */
function Lightbox({ shots, index, onIndex, onClose, src = shotUrl }) {
  const shot = shots[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex(Math.min(shots.length - 1, index + 1));
      if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, shots.length, onIndex, onClose]);
  if (!shot) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Screenshot">
      <div className="flex flex-none items-center gap-3 px-5 py-3 text-sm text-slate-200">
        <span className={`h-2.5 w-2.5 rounded-full ${STEP_TONE[shot.status]?.solid || "bg-slate-400"}`} />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-semibold">Step {shot.step}</span> · {shot.phase === "before" ? "Before" : "After"} — {shot.label}
        </span>
        <span className="text-xs tabular-nums text-slate-400">
          {index + 1} / {shots.length}
        </span>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-slate-300 hover:bg-white/10">
          <Icons.X className="h-5 w-5" />
        </button>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-14 pb-6" onClick={onClose}>
        <img src={src(shot.key)} alt={`Step ${shot.step} ${shot.phase}`} className="max-h-full max-w-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        {index > 0 && (
          <button
            type="button"
            aria-label="Previous screenshot"
            onClick={(e) => {
              e.stopPropagation();
              onIndex(index - 1);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
          >
            <Icons.ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {index < shots.length - 1 && (
          <button
            type="button"
            aria-label="Next screenshot"
            onClick={(e) => {
              e.stopPropagation();
              onIndex(index + 1);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
          >
            <Icons.ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}

function Shot({ label, evKey, onOpen, src = shotUrl }) {
  if (!evKey) {
    return (
      <div className="flex h-36 flex-1 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-[11px] text-slate-400">
        No {label.toLowerCase()} screenshot
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`Open the ${label.toLowerCase()} screenshot`}
      className="group relative h-36 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition hover:border-brand-400 hover:shadow-md"
    >
      <img src={src(evKey)} alt={`${label} screenshot`} loading="lazy" className="h-full w-full object-cover object-top" />
      <span className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">{label}</span>
      <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/20 group-hover:opacity-100">
        <Icons.Maximize className="h-5 w-5 text-white" />
      </span>
    </button>
  );
}

function StepCard({ s, firstFailed, refFn, onShot, src }) {
  const tone = STEP_TONE[s.status] || STEP_TONE.not_run;
  const english = s.step ? stepToEnglish(s.step) : s.name;
  const script = s.step ? stepToScript(s.step) : "";
  return (
    <li ref={refFn} className={`scroll-mt-4 rounded-xl border bg-card p-4 shadow-sm ${tone.row} ${firstFailed ? "ring-2 ring-rose-400/40" : ""}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-bold text-white ${tone.solid}`} title={tone.label}>
          {s.status === "not_run" ? s.index : <tone.Icon className="h-3.5 w-3.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold tabular-nums text-slate-400">Step {s.index}</span>
            {s.session && (
              <span className="rounded bg-slate-100 px-1.5 py-px text-xs font-semibold text-slate-600" title="The browser window this step acted in">
                Session {s.session}
              </span>
            )}
            {firstFailed && <span className="rounded bg-rose-600 px-1.5 py-px text-[10px] font-bold uppercase tracking-wider text-white">First failure</span>}
            {s.step?.precondition_check && (
              <span className="rounded bg-amber-100 px-1.5 py-px text-[10px] font-bold uppercase tracking-wider text-amber-800" title="The first step that depends on the test's preconditions">
                Precondition check
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[13px] font-medium text-slate-800">{english || "(no description)"}</p>
          {script && <p className="mt-0.5 break-all font-mono text-[11px] text-slate-400">{script}</p>}
        </div>
        <span className={`inline-flex flex-none items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tone.pill}`}>{tone.label}</span>
      </div>
      {s.status === "failed" && <p className="mt-3 text-[13px] text-rose-900">{explainFailure(s.step || {}, s.error).why}</p>}
      {s.error && (
        /* A skipped step's reason is not an error: neutral, not red. */
        <pre
          className={`mt-3 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg border px-3 py-2 font-mono text-[11px] leading-relaxed ${
            s.status === "skipped"
              ? "border-slate-200 bg-slate-50 text-slate-600"
              : s.status === "blocked"
                ? "border-amber-200 bg-amber-50 text-amber-900"
                : "border-rose-200 bg-rose-50 text-rose-800"
          }`}
        >
          {s.error}
        </pre>
      )}
      {(s.before || s.after || s.status !== "not_run") && (
        <div className="mt-3 flex gap-3">
          <Shot label="Before" evKey={s.before} onOpen={() => onShot(s.index, "before")} src={src} />
          <Shot label="After" evKey={s.after} onOpen={() => onShot(s.index, "after")} src={src} />
        </div>
      )}
    </li>
  );
}

const FAULT_TEXT = {
  artifact: "Most likely a problem in the Artifact (the script), not in the app — generate it again or fix the step.",
  app: "The app did not behave as the test expects — this may be a defect.",
};

/* The first failure, spelled out for whoever reads the result: the test and
   the Cucumber line the step was carrying out, the step itself, and why it
   failed in plain words (the browser's own error stays under "Details"). */
function FailureSummary({ test, step: s, total, failedCount, onGo }) {
  const st = s.step || {};
  const line = (test?.xray_steps || []).find((x) => x.step_no === st.source_step_no) || null;
  const { why, fault } = explainFailure(st, s.error);
  const pre = st.precondition_check ? test?.preconditions || [] : [];
  const row = (label, children) => (
    <div className="grid grid-cols-[7.5rem_1fr] gap-3 py-2">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-rose-500">{label}</dt>
      <dd className="min-w-0 text-[13px] text-rose-900">{children}</dd>
    </div>
  );
  return (
    <section aria-label="Why this execution failed" className="rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-3">
      <div className="flex items-start gap-3">
        <Icons.Alert className="mt-0.5 h-5 w-5 flex-none text-rose-600" />
        <p className="min-w-0 flex-1 text-[13px] font-semibold text-rose-900">
          Failed at step {s.index} of {total}
          {failedCount > 1 && <span className="font-normal text-rose-700"> · {failedCount} steps failed in total</span>}
        </p>
        <button
          type="button"
          onClick={onGo}
          className="flex-none rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-rose-700 hover:bg-rose-50"
        >
          Go to step {s.index}
        </button>
      </div>
      <dl className="mt-2 divide-y divide-rose-200/70 pl-8">
        {row(
          "Test",
          <>
            <span className="font-semibold">{test?.xray_test_key || "—"}</span>
            {test?.name && <span> — {test.name}</span>}
            {line && (
              <span className="mt-1 block text-rose-800">
                Carrying out line {line.step_no}:{" "}
                {line.keyword && <span className="font-mono font-semibold text-blue-700">{line.keyword} </span>}
                {line.action}
              </span>
            )}
          </>,
        )}
        {row(
          "It tried to",
          <>
            {s.step ? stepToEnglish(st) : s.name}
            {stepToScript(st) && <span className="mt-0.5 block break-all font-mono text-[11px] text-rose-700/80">{stepToScript(st)}</span>}
          </>,
        )}
        {row(
          "Why it failed",
          <>
            {why}
            {pre.length > 0 && (
              <span className="mt-1 block">
                This step checks the test's precondition, so it was probably not met: {pre.join(" ")}
              </span>
            )}
            {FAULT_TEXT[fault] && <span className="mt-1 block text-[12px] text-rose-700">{FAULT_TEXT[fault]}</span>}
            {s.error && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[12px] font-semibold text-rose-700">Details (browser error)</summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-rose-800">{s.error}</pre>
              </details>
            )}
          </>,
        )}
      </dl>
    </section>
  );
}

/* "Go to top": shown once the page is scrolled down (the steps run long). It
   scrolls the nearest scrolling parent of `anchor` (MainPage's <main>), else
   the window. */
function GoTopButton({ anchor }) {
  const [shown, setShown] = useState(false);
  const scroller = useRef(null);
  useEffect(() => {
    let el = anchor.current?.parentElement || null;
    while (el && !/(auto|scroll)/.test(window.getComputedStyle(el).overflowY)) el = el.parentElement;
    const target = el || window;
    scroller.current = target;
    const read = () => setShown((el ? el.scrollTop : window.scrollY) > 400);
    read();
    target.addEventListener("scroll", read, { passive: true });
    return () => target.removeEventListener("scroll", read);
  }, [anchor]);
  if (!shown) return null;
  return (
    <button
      type="button"
      onClick={() => scroller.current?.scrollTo?.({ top: 0, behavior: "smooth" })}
      aria-label="Go to top"
      title="Go to top"
      className="fixed bottom-6 right-6 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg ring-1 ring-brand-700/20 transition hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
    >
      <Icons.ChevronUp className="h-5 w-5" />
    </button>
  );
}

/* `fetchResult`, `shotSrc`, `backLabel` and `renderLive` let another screen
   (App Pages › Executions) show its executions in this same view: it hands
   in data of this shape, how to load a screenshot, and a live panel shown
   while the execution runs. `renderFooter(data, reload)` adds a panel above
   the steps (For you › Test Runs: review and submit). `onDelete(data)` (the
   Artifacts screen) puts a 🗑 at the top right of a trial run that is not
   running; the screen asks, deletes it and goes back. */
export default function ExecutionResultView({
  executionkey,
  onBack,
  fetchResult,
  shotSrc,
  backLabel = "Artifacts",
  renderLive,
  renderNotice,
  renderFooter,
  onDelete,
}) {
  const src = shotSrc || shotUrl;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [bugFor, setBugFor] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const stepRefs = useRef({});
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    try {
      if (fetchResult) {
        setData((await fetchResult()) || null);
        setError("");
        return;
      }
      const res = await fetch(`${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/result`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setData(body?.data || null);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the execution.");
    }
  }, [executionkey, fetchResult]);

  useEffect(() => {
    load();
  }, [load]);

  /* Still executing → keep it fresh. (A trial sends nothing to Jira, so
     nothing more arrives once it has finished.) */
  const pending =
    data &&
    (ACTIVE.has(data.run_status) ||
      /* App Page executions: the AI is still repairing a failed step. */
      ["pending", "fixing"].includes(data.ai_fix?.status));
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [pending, load]);

  const test = data?.tests?.[0] || null;
  const steps = useMemo(() => test?.steps || [], [test]);
  const failedSteps = steps.filter((s) => s.status === "failed");
  const notRun = steps.filter((s) => s.status === "not_run").length;
  const skippedN = steps.filter((s) => s.status === "skipped").length;
  /* A step that could not be run as things stand (an App Page with no menu item): not a failure. */
  const blockedN = steps.filter((s) => s.status === "blocked").length;
  const shown = filter === "failed" ? failedSteps : steps;
  const shots = useMemo(() => {
    const out = [];
    for (const s of filter === "failed" ? steps.filter((x) => x.status === "failed") : steps) {
      const label = s.step ? stepToEnglish(s.step) : s.name;
      if (s.before) out.push({ key: s.before, step: s.index, phase: "before", status: s.status, label });
      if (s.after) out.push({ key: s.after, step: s.index, phase: "after", status: s.status, label });
    }
    return out;
  }, [steps, filter]);
  const openShot = (step, phase) => {
    const i = shots.findIndex((x) => x.step === step && x.phase === phase);
    if (i >= 0) setLightbox(i);
  };

  if (error && !data) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} label={backLabel} />
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <BackLink onBack={onBack} label={backLabel} />
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Icons.Spinner className="h-4 w-4 animate-spin" /> Loading the execution…
        </p>
      </div>
    );
  }

  const tone = RUN_TONE[data.run_status] || RUN_TONE[test?.result_status] || RUN_TONE.cancelled;
  /* Who raised it: its Reporter in Jira, else the Cortex user who created it. */
  const runBy = data.xray_test_execution_reporter || data.created_by || "";
  const started = test?.started_at || data.started_at;
  const finished = test?.finished_at || data.finished_at;
  const total = steps.length;
  const passedN = test?.passed_steps || 0;
  const firstFailed = test?.failed_step || null;
  const firstFailedStep = steps.find((s) => s.index === firstFailed);
  const canBug = test && ["failed", "blocked"].includes(test.result_status) && !test.bug_key && !ACTIVE.has(data.run_status);

  return (
    <div ref={rootRef} className="mx-auto max-w-5xl space-y-4 pb-10">
      <BackLink onBack={onBack} label={backLabel} />
      <GoTopButton anchor={rootRef} />

      {/* Header — the execution in one card */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
        <div className="flex items-start gap-4 p-5">
          <span className="relative flex-none" title={tone.label}>
            {/* A trial run as its card shows it (▶); a formal run is a Jira Test Execution. */}
            {data.run_mode === "trial" ? <RunIcon /> : <Icons.JiraTestExecution />}
            <span className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-card ${tone.solid}`}>
              <tone.Icon className={`h-3 w-3 text-white ${tone.spin ? "animate-spin" : ""}`} />
            </span>
          </span>
          <div className="min-w-0 flex-1">
            {/* The Artifact that ran (App Page executions hand it in): ART-RD-1 · its title. */}
            {(data.artifact?.artifact_key || data.artifact?.title) && (
              <h2 className="mb-1 flex min-w-0 items-center gap-1.5 text-[13px] font-semibold text-slate-600">
                <Icons.ScrollText className="h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
                {data.artifact.artifact_key && <span className="flex-none text-blue-700">{data.artifact.artifact_key}</span>}
                {data.artifact.artifact_key && data.artifact.title && <span className="flex-none text-slate-300">·</span>}
                {data.artifact.title && (
                  <span className="min-w-0 truncate" title={data.artifact.title}>
                    {data.artifact.title}
                  </span>
                )}
              </h2>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {data.xray_test_execution_key ? (
                <a
                  href={data.xray_test_execution_url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the Test Execution in Jira"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900/[0.04] px-1.5 py-0.5 font-mono text-[12px] font-semibold text-blue-700 ring-1 ring-inset ring-slate-900/10 hover:bg-blue-50 hover:ring-blue-300"
                >
                  {data.xray_test_execution_key}
                  <Icons.Link className="h-3 w-3 opacity-60" />
                </a>
              ) : (
                ACTIVE.has(data.run_status) && <span className="text-[11px] text-slate-400">Executing…</span>
              )}
              {/* A trial run (Artifacts, Requirement Board): never evidence. */}
              {data.run_mode === "trial" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-500/20">
                  <Icons.Script className="h-3 w-3" aria-hidden="true" />
                  {TRIAL_LABEL}
                  {data.read_only_note ? ` · ${data.read_only_note}` : ""}
                </span>
              )}
              <h1 className="min-w-0 text-[18px] font-bold tracking-tight text-slate-900">{data.name}</h1>
            </div>
            {data.description && <p className="mt-1 text-[13px] text-slate-500">{data.description}</p>}
            {/* Blocked: which precondition was not met, or what stopped the
                run before step 1, in words. */}
            {test?.result_status === "blocked" && test.blocked_note && (
              <p
                role="note"
                className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] leading-snug text-amber-900 ring-1 ring-inset ring-amber-600/20"
              >
                <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                <span className="min-w-0 break-words">
                  <span className="font-semibold">
                    {test.blocked_reason === "not_started" ? "Not tested: " : "Blocked: "}
                  </span>
                  {test.blocked_note}
                </span>
              </p>
            )}
            {/* PlantOps may be left changed, whatever the test's result. */}
            {test?.cleanup_note && (
              <p
                role="note"
                className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm leading-snug text-amber-900 ring-1 ring-inset ring-amber-600/20"
              >
                <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                <span className="min-w-0 break-words">{test.cleanup_note}</span>
              </p>
            )}
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-slate-500">
              {test?.xray_test_key && <LinkedChip kind="Test" issueKey={test.xray_test_key} />}
              {(test?.story_key || data.story_key) && <LinkedChip kind="Story" issueKey={test?.story_key || data.story_key} />}
              {test?.bug_key && <LinkedChip kind="Bug" issueKey={test.bug_key} href={test.bug_url || undefined} />}
              {runBy && (
                <span className="flex items-center gap-1.5 text-slate-600">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-[9px] font-semibold uppercase text-white">{initials(runBy)}</span>
                  {runBy}
                </span>
              )}
              {started && (
                <span className="inline-flex items-center gap-1">
                  <Icons.Calendar className="h-3.5 w-3.5 text-slate-400" /> {fmtWhen(started)}
                </span>
              )}
              {fmtDuration(started, finished) && (
                <span className="inline-flex items-center gap-1">
                  <Icons.Clock className="h-3.5 w-3.5 text-slate-400" /> {fmtDuration(started, finished)}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-none items-center gap-2">
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-[12px] font-semibold ring-1 ring-inset ${tone.pill}`}>{tone.label}</span>
            {canBug && (
              <button
                type="button"
                onClick={() => setBugFor(test)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-[13px] font-semibold text-rose-700 shadow-sm hover:border-rose-300 hover:bg-rose-50"
              >
                Raise a defect
              </button>
            )}
            {onDelete && data.run_mode === "trial" && !ACTIVE.has(data.run_status) && (
              <button
                type="button"
                onClick={() => onDelete(data)}
                title="Delete this trial run"
                aria-label="Delete this trial run"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
              >
                <Icons.Trash className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Step summary */}
        {total > 0 && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
            <div className="flex min-w-[12rem] flex-1 items-center gap-3">
              <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                <span className="h-full bg-emerald-500" style={{ width: `${(passedN / total) * 100}%` }} />
                <span className="h-full bg-rose-500" style={{ width: `${(failedSteps.length / total) * 100}%` }} />
                {blockedN > 0 && <span className="h-full bg-amber-600" style={{ width: `${(blockedN / total) * 100}%` }} />}
                <span className="h-full bg-amber-400" style={{ width: `${(skippedN / total) * 100}%` }} />
              </div>
            </div>
            <span className="text-[12px] text-slate-600">
              <span className="font-semibold text-emerald-700">{passedN}</span> passed · <span className="font-semibold text-rose-700">{failedSteps.length}</span> failed
              {blockedN > 0 && (
                <>
                  {" "}
                  · <span className="font-semibold text-amber-700">{blockedN}</span> blocked
                </>
              )}
              {skippedN > 0 && (
                <>
                  {" "}
                  · <span className="font-semibold text-amber-700">{skippedN}</span> skipped
                </>
              )}
              {notRun > 0 && (
                <>
                  {" "}
                  · <span className="font-semibold text-slate-700">{notRun}</span> not executed
                </>
              )}{" "}
              <span className="text-slate-400">of {total} steps</span>
            </span>
          </div>
        )}
      </section>

      {ACTIVE.has(data.run_status) && (
        <p className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <Icons.Spinner className="h-4 w-4 animate-spin" /> This execution is still running — the steps fill in as it goes.
        </p>
      )}
      {renderNotice?.(data)}
      {ACTIVE.has(data.run_status) && renderLive?.(data)}
      {data.xray_push_error && <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800">Jira: {data.xray_push_error}</p>}


      {/* Where it failed: which test, what it was doing, and why it failed */}
      {firstFailedStep && (
        <FailureSummary
          test={test}
          step={firstFailedStep}
          total={total}
          failedCount={failedSteps.length}
          onGo={() => stepRefs.current[firstFailedStep.index]?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
      )}

      {renderFooter?.(data, load)}

      {/* Steps */}
      {total > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Steps</h2>
            <div role="tablist" aria-label="Filter steps" className="inline-flex rounded-lg bg-slate-100 p-0.5">
              {[
                ["all", "All", total],
                ["failed", "Failed", failedSteps.length],
              ].map(([k, label, n]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={filter === k}
                  onClick={() => setFilter(k)}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-[12px] font-semibold transition",
                    filter === k ? "bg-card text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800",
                  ].join(" ")}
                >
                  {label}
                  <span className="rounded-full bg-slate-200/80 px-1.5 text-[10px] tabular-nums text-slate-600">{n}</span>
                </button>
              ))}
            </div>
          </div>
          <ol className="space-y-3">
            {shown.map((s) => (
              <StepCard
                key={s.index}
                s={s}
                firstFailed={s.index === firstFailed}
                refFn={(el) => {
                  stepRefs.current[s.index] = el;
                }}
                onShot={openShot}
                src={src}
              />
            ))}
            {shown.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No failed steps.</p>}
          </ol>
        </section>
      )}

      {lightbox !== null && <Lightbox shots={shots} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} src={src} />}
      {bugFor && (
        <RaiseBugModal
          executionkey={executionkey}
          linkkey={bugFor.linkkey}
          caseName={bugFor.name}
          onClose={() => setBugFor(null)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}

function BackLink({ onBack, label = "Artifacts" }) {
  return (
    <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-800">
      <Icons.ArrowLeft className="h-4 w-4" /> {label}
    </button>
  );
}
